// Real customer stories, one entry per approved story. Empty until the
// first customers approve publication — the hub shows a designed honest
// note while this list is empty. Shape matches the internal mockup:
// metric-led headline, persona, before/after, and the features that
// drove the result.

export interface CustomerStory {
  slug: string;
  metric: string;
  headline: string;
  persona: string;
  company: string;
  size: string;
  industry: string;
  quote: string;
  quoteName: string;
  quoteRole: string;
  before: readonly string[];
  after: readonly string[];
  features: readonly string[];
  summary: string;
}

export const customerStories: readonly CustomerStory[] = [];

export const customerStoriesBySlug: Record<string, CustomerStory> = {};
for (const story of customerStories) {
  customerStoriesBySlug[story.slug] = story;
}

// Real App Store reviews of Workspace369 (app id 6475013419), copied from
// Apple's public customer-reviews feed. Quotes are verbatim: never edit,
// translate, fix typos in or shorten them. Names are the reviewers' public
// App Store usernames. Only reviews that exist in Apple's feed may be added:
// each entry needs reviewId, sourceUrl and date (enforced by
// tests/reviews/app-store-reviews.test.ts). Excluded on purpose: reviews by
// the founder or other insiders (FTC 16 CFR 465 / Endorsement Guides).
// Feed: https://itunes.apple.com/us/rss/customerreviews/page=1/id=6475013419/sortby=mostrecent/json
export const APP_STORE_LISTING_URL = 'https://apps.apple.com/app/id6475013419';
export const APP_STORE_REVIEWS_SOURCE_URL =
  'https://apps.apple.com/us/app/id6475013419?see-all=reviews';

// Aggregate from https://itunes.apple.com/lookup?id=6475013419 (averageUserRating,
// userRatingCount), fetched on asOf. Update all three together.
export const appStoreRating = {
  average: 5,
  count: 12,
  asOf: '2026-10-26',
} as const;

export interface AppStoreReview {
  reviewId: string;
  sourceUrl: string;
  author: string;
  title: string;
  stars: number;
  quote: string;
  date: string;
  storefront: string;
}

export const appStoreReviews: readonly AppStoreReview[] = [
  {
    reviewId: "14620126834",
    sourceUrl: APP_STORE_REVIEWS_SOURCE_URL,
    author: "Foreman345",
    title: "SMB Modernization",
    stars: 5,
    date: "2026-10-02",
    storefront: "us",
    quote: "Great tool for modernizing small businesses. Ability to leverage AI without technical background.",
  },
  {
    reviewId: "14619603232",
    sourceUrl: APP_STORE_REVIEWS_SOURCE_URL,
    author: "JESSICANN2026",
    title: "Streamlined CRM",
    stars: 5,
    date: "2026-10-02",
    storefront: "us",
    quote: "Workspace369 has been a game changer for keeping everything in one place. I can easily keep track of clients, follow-ups, team tasks, scheduling, and billing without having to jump between multiple platforms. The interface is really intuitive, and having everything connected makes it much easier to see what needs to get done and stay on top of deadlines. It’s also been great for keeping our team aligned and making sure nothing falls through the cracks.",
  },
  {
    reviewId: "14619598940",
    sourceUrl: APP_STORE_REVIEWS_SOURCE_URL,
    author: "samimonroe",
    title: "True all-in-one system",
    stars: 5,
    date: "2026-10-02",
    storefront: "us",
    quote: "It beautifully combined my CRM, team calendar, and billing tools so I could finally stop paying for a bunch of separate software subscriptions",
  },
  {
    reviewId: "11985154183",
    sourceUrl: APP_STORE_REVIEWS_SOURCE_URL,
    author: "SebastianBroco",
    title: "Best app for my construction business",
    stars: 5,
    date: "2024-11-23",
    storefront: "us",
    quote: "Great app. This app solved all my needs! Not only did it help, but it is cheaper than other apps. I highly recommend this app if you want to start organizing and automating your business on another level. Also love the design",
  },
  {
    reviewId: "11606351847",
    sourceUrl: APP_STORE_REVIEWS_SOURCE_URL,
    author: "Martynaa d",
    title: "I manage my gym facility with this app",
    stars: 5,
    date: "2024-08-13",
    storefront: "us",
    quote: "Very easy to use. Amazing app",
  },
  {
    reviewId: "11594285579",
    sourceUrl: APP_STORE_REVIEWS_SOURCE_URL,
    author: "YABO02",
    title: "Outstanding",
    stars: 5,
    date: "2024-08-10",
    storefront: "us",
    quote: "Very easy and fast. Great application, wow.",
  },
  {
    reviewId: "11592061789",
    sourceUrl: APP_STORE_REVIEWS_SOURCE_URL,
    author: "Long horn wine",
    title: "The best on the market",
    stars: 5,
    date: "2024-08-09",
    storefront: "us",
    quote: "This is the best app to run a business, i tested all the apps and this is the best one.",
  },
];

// A fixed set of three short reviews for compact spots (home, solution pages,
// demo dialog). /customers lists every review in the array above.
export const featuredAppStoreReviewIds = [
  "14620126834",
  "14619598940",
  "11985154183",
] as const;

export const featuredAppStoreReviews: readonly AppStoreReview[] = featuredAppStoreReviewIds.map((id) => {
  const review = appStoreReviews.find((item) => item.reviewId === id);
  if (!review) throw new Error(`Unknown App Store review id ${id}`);
  return review;
});
